import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar } from "lucide-react";
import api from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1590247813693-5541d1c609fd?auto=format&fit=crop&w=2400&q=85";

export default function Projects() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("All");

  useEffect(() => {
    api.get("/projects").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
  const filtered = category === "All" ? items : items.filter((i) => i.category === category);

  return (
    <div>
      {/* Hero */}
      <section className="py-16 lg:py-14 lg:py-16 bg-[color:var(--mia-grey)]">
        <div className="mia-container grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[color:var(--mia-navy)] tracking-tight leading-tight">PROJECTS</h1>
            <h2 className="font-display font-bold text-lg sm:text-xl text-[color:var(--mia-gold)] tracking-wide uppercase mt-4">
              Delivering Impact Across Africa's Waterfronts.
            </h2>
            <div className="w-16 h-1 bg-[color:var(--mia-gold)] my-6" />
            <p className="text-[color:var(--mia-text)] leading-relaxed max-w-lg">
              Explore a selection of our projects delivered across Africa — from coastal protection and floating
              structures to waterfront developments and marine access infrastructure.
            </p>
          </div>
          <div className="rounded-md overflow-hidden aspect-[4/3] shadow-2xl">
            <img src={HERO_IMG} alt="Featured projects" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-10 border-b border-slate-100 bg-white">
        <div className="mia-container flex flex-wrap gap-2" data-testid="projects-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-md text-sm font-semibold border-2 transition-colors ${
                c === category
                  ? "bg-[color:var(--mia-navy)] text-white border-[color:var(--mia-navy)]"
                  : "bg-white text-[color:var(--mia-navy)] border-slate-200 hover:border-[color:var(--mia-navy)]"
              }`}
              data-testid={`project-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="py-14 lg:py-16">
        <div className="mia-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((p) => (
            <Link
              key={p.id}
              to={`/projects/${p.slug}`}
              className="group bg-white rounded-md overflow-hidden card-lift border border-slate-100"
              data-testid={`project-card-${p.slug}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover image-zoom" />
              </div>
              <div className="p-6">
                <div className="text-[0.7rem] font-bold tracking-[0.16em] uppercase text-[color:var(--mia-gold)]">
                  {p.category}
                </div>
                <h3 className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-wide uppercase mt-2 leading-tight">{p.title}</h3>
                <div className="mt-3 flex items-center gap-4 text-xs text-[color:var(--mia-muted)]">
                  <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {p.location}</span>
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {p.year}</span>
                </div>
                <p className="text-sm text-[color:var(--mia-muted)] mt-3 leading-relaxed line-clamp-3">{p.overview}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-[color:var(--mia-navy)]">
                  View Project <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[color:var(--mia-navy)] text-white">
        <div className="mia-container text-center max-w-2xl mx-auto">
          <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight">
            Let's build your next waterfront project together.
          </h3>
          <div className="mt-6 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="mia-btn mia-btn-primary" data-testid="projects-cta-consultation">Request A Consultation</Link>
            <Link to="/solutions" className="mia-btn mia-btn-outline-white" data-testid="projects-cta-solutions">View Our Services</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
